import { useCallback, useEffect, useMemo } from 'react'
import Image from 'next/image'
import clsx from 'clsx'

import styles from './_.module.css'

interface Props {
  list: {
    description?: string | null
    color?: string | null
    image?: StrapiImageFile
  }[]
}

const TIMEOUT_DURATION = 2000

// TODO: @amar-peppercontent
// - have same markup for desktop & mobile
// - don't have svg elements directly inside component
function Infograph({ list }: Props): JSX.Element {
  let imageTopPosition: number
  const timers: NodeJS.Timeout[] = useMemo(() => [], [])

  const handleDesktopInfographAnimation = useCallback(() => {
    // svg content path observer
    const contentPathObserver = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          // make path active
          entries[0].target.classList.add('active')

          //make circle active
          desktopCircleElement?.classList.add('active')

          revealImages.forEach((element, index) => {
            //set delay for images on path
            const timerId = setTimeout(() => {
              revealImagesObserver.observe(element)
              timers.push(timerId)
            }, TIMEOUT_DURATION * index + 1)
          })
        }
      },
      { threshold: 0.1 }
    )
    const contentPathElement = document.querySelector('.content_path')
    const desktopCircleElement = document.querySelector('.desktop_circle')
    const revealImages = document.querySelectorAll('.reveal')

    contentPathElement && contentPathObserver.observe(contentPathElement)

    // images observer
    const revealImagesObserver = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          // make images reveal
          entries[0].target.classList.add('active')

          //make content reveal with images
          entries[0].target.nextElementSibling?.classList.add('active')
        }
      },
      { threshold: 0.5 }
    )
  }, [timers])

  const handleMobileInfographAnimation = useCallback(() => {
    // svg content path observer
    const contentPathObserver = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          // make path active
          entries[0].target.classList.add('active')

          //make circle active
          mobileCircleElement?.classList.add('active')

          const timerId = setTimeout(() => {
            revealImages.forEach(element => {
              //set delay for images on path
              revealImagesObserver.observe(element)
              timers.push(timerId)
            })
          }, TIMEOUT_DURATION)
        }
      },
      { threshold: 0 }
    )
    const mobileContentPathElement = document.querySelector(
      '.mobile_content_path'
    ) as HTMLElement
    const mobileCircleElement = document.querySelector(
      '.mobile_circle'
    ) as HTMLElement
    const revealImages = document.querySelectorAll('.mobileReveal')

    mobileContentPathElement &&
      contentPathObserver.observe(mobileContentPathElement)

    // images observer
    const revealImagesObserver = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          // make images reveal
          entries[0].target.classList.add('active')

          //make content reveal with images
          entries[0].target.nextElementSibling?.classList.add('active')
        }
      },
      { threshold: 1 }
    )
  }, [timers])

  useEffect(() => {
    handleDesktopInfographAnimation()
    handleMobileInfographAnimation()
    return () => {
      timers.forEach(timer => clearTimeout(timer))
    }
  }, [handleDesktopInfographAnimation, handleMobileInfographAnimation, timers])

  return (
    <div className="bg-blue-web-90 text-white | pt-5 pb-18 md:py-24">
      <div
        className={clsx(
          'max-w-[83.5rem] | mx-auto | relative | hidden xl:block overflow-x-scroll',
          styles.scrollInvisible
        )}
      >
        <div className={clsx(styles.content_path, 'content_path')}>
          {/* svg path for desktop */}
          <svg
            width="1336"
            height="1969"
            viewBox="0 -10 1336 1890"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className={styles.path}
              fill="none"
              stroke="#fff"
              strokeLinejoin="round"
              strokeMiterlimit="10"
              id="wire"
              d="M-2 2H216C250.242 2 278 29.7583 278 64V164.5C278 198.742 305.758 226.5 340 226.5H782.981C817.222 226.5 844.981 254.258 844.981 288.5V349.5C844.981 383.742 817.222 411.5 782.981 411.5H426.314C392.072 411.5 364.314 439.258 364.314 473.5V804.5C364.314 838.742 392.072 866.5 426.314 866.5H794C828.242 866.5 856 894.258 856 928.5V1062.5C856 1096.74 828.242 1124.5 794 1124.5H415.114C380.873 1124.5 353.114 1152.26 353.114 1186.5V1388C353.114 1422.24 380.873 1450 415.114 1450H704C738.242 1450 766 1477.76 766 1512V1805C766 1839.24 793.758 1867 828 1867H1333"
            />
            <path
              className={styles.dashed}
              fill="none"
              stroke="#070425"
              strokeWidth="4"
              strokeLinejoin="round"
              strokeMiterlimit="10"
              d="M-2 2H216C250.242 2 278 29.7583 278 64V164.5C278 198.742 305.758 226.5 340 226.5H782.981C817.222 226.5 844.981 254.258 844.981 288.5V349.5C844.981 383.742 817.222 411.5 782.981 411.5H426.314C392.072 411.5 364.314 439.258 364.314 473.5V804.5C364.314 838.742 392.072 866.5 426.314 866.5H794C828.242 866.5 856 894.258 856 928.5V1062.5C856 1096.74 828.242 1124.5 794 1124.5H415.114C380.873 1124.5 353.114 1152.26 353.114 1186.5V1388C353.114 1422.24 380.873 1450 415.114 1450H704C738.242 1450 766 1477.76 766 1512V1805C766 1839.24 793.758 1867 828 1867H1333"
            />
            <circle
              className={clsx(styles.desktop_circle, 'desktop_circle')}
              r="10.5"
              fill="white"
              stroke="white"
            ></circle>
          </svg>
        </div>
        {list &&
          list.slice(0, 6).map((element, key) => {
            imageTopPosition =
              key === 0
                ? 90
                : imageTopPosition + (element.image?.height ?? 0) + 45
            return (
              <div
                key={`desktop-infograph-${key}`}
                className={clsx(
                  `absolute | flex items-center infograph_images`
                )}
                style={{
                  top: `${imageTopPosition}px`,
                  left: `${key % 2 === 0 ? '10rem' : '40rem'}`
                }}
              >
                <div
                  className={clsx(
                    styles.reveal,
                    `reveal | opacity-0 | flex-none ${
                      key % 2 === 0 ? 'fade-left' : 'fade-right'
                    }`
                  )}
                >
                  {element.image ? (
                    <Image
                      src={element.image.url}
                      alt="image"
                      width={element.image.width}
                      height={element.image.height}
                      className="max-w-xs"
                    />
                  ) : null}
                </div>
                <div
                  className={clsx(
                    `flex-none | opacity-0 | revealContent | border-l-2 | ml-8 pl-5 | max-w-[14rem] text-2xl`,
                    styles.revealContent
                  )}
                  style={{ borderColor: element.color ?? '' }}
                  dangerouslySetInnerHTML={{
                    __html: element.description ?? ''
                  }}
                ></div>
              </div>
            )
          })}
      </div>

      {/* svg path for mobile */}
      <div className="xl:hidden | relative | py-10 md:py-24">
        <div
          className={clsx(styles.mobile_content_path, 'mobile_content_path')}
        >
          <svg
            className="mx-auto"
            width="360"
            height="2286"
            viewBox="0 -10 360 2126"
            fill="none"
          >
            <path
              className={styles.path}
              fill="none"
              stroke="#fff"
              strokeLinejoin="round"
              strokeMiterlimit="10"
              id="wire"
              d="M-559.864 2.43945H131.615C160.334 2.43945 183.615 25.7206 183.615 54.4395V2052.25C183.615 2080.97 206.896 2104.25 235.615 2104.25H837.601"
            />
            <path
              className={styles.dashed}
              fill="none"
              stroke="#070425"
              strokeWidth="4"
              strokeLinejoin="round"
              strokeMiterlimit="10"
              d="M-559.864 2.43945H131.615C160.334 2.43945 183.615 25.7206 183.615 54.4395V2052.25C183.615 2080.97 206.896 2104.25 235.615 2104.25H837.601"
            />

            <circle
              className={clsx(styles.mobile_circle, 'mobile_circle')}
              r="10.5"
              fill="white"
              stroke="white"
              path="M-559.864 2.43945H131.615C160.334 2.43945 183.615 25.7206 183.615 54.4395V2052.25C183.615 2080.97 206.896 2104.25 235.615 2104.25H837.601"
            ></circle>
            {/* <circle className={styles.circle} r="9.5" fill="white" stroke="white">
              <animateMotion dur="20s" repeatCount="indefinite" begin="10s">
                <mpath xlinkHref="#mobile-wire"></mpath>
              </animateMotion>
            </circle> */}
          </svg>
        </div>
        <div className="flex flex-col gap-14 justify-center items-center | absolute top-20 left-1/2 -translate-x-1/2 | w-full">
          {list &&
            list.slice(0, 6).map((element, key) => {
              return (
                <div
                  key={`mobile-infograph-${key}`}
                  className="flex flex-col-reverse gap-5 justify-items-end"
                >
                  <div
                    className={clsx(
                      `mobileReveal | opacity-0 | flex-none ${
                        key % 2 === 0 ? 'fade-left' : 'fade-right'
                      }`,
                      styles.reveal
                    )}
                  >
                    {element.image ? (
                      <Image
                        src={element.image.url}
                        alt="image"
                        width={element.image.width}
                        height={element.image.height}
                        className="h-auto w-[19rem]"
                      />
                    ) : null}
                  </div>
                  <div
                    className={clsx(
                      `opacity-0 | mobileRevealContent | text-sm | max-w-[6.625rem] ${
                        key % 2 === 0
                          ? ' border-l-2 | mr-auto ml-4 pl-3 '
                          : 'ml-auto mr-4 pr-3 | border-r-2'
                      }`,
                      styles.revealContent
                    )}
                    style={{ borderColor: element.color ?? '' }}
                    dangerouslySetInnerHTML={{
                      __html: element.description ?? ''
                    }}
                  ></div>
                </div>
              )
            })}
        </div>
      </div>
    </div>
  )
}

export default Infograph
